import dataModel from '../../models/DataModel.js';
import { RecordStates } from '../../constants/ReduxConstants';
import _ from 'lodash';
import StoreItemProcessing from '../../helpers/StoreFunctions/StoreItemProcessing';
import miscFunctions from '../../helpers/misc-functions.js';

/**
 * Superclass for all other redux Collections.
 */

export default class AOCollection {

  constructor(options = {}) {
    const { name, collectionType, perPage, page, searchString, sortField, filter, userId, companyId, subKey, recordDescriptor } = options;
    this.name = name;
    this.collectionType = collectionType;
    this.perPage = perPage;
    this._searchString = searchString;
    this.sortField = sortField;
    this.ascending = true;
    this._filter = filter || {};
    this.userId = userId;
    this.companyId = companyId;
    this.subKey = subKey && subKey.length > 0 ? subKey.replace(',', '_') : subKey;
    this.page = page || 1;
    this.recordDescriptor = recordDescriptor;
    this._shouldRefreshItem = this._shouldRefreshItem.bind(this);
    const collection = dataModel.collectionsDAO.findCollection(this.getKey());
    if (collection){
      this.ids = collection.ids;
    }
    else{
      this.ids = [];
    }
  }

  add(id) {
    const collectionData = dataModel.collectionsDAO.findCollection(this.getKey());
    if (!collectionData.ids) {
      collectionData.ids = [];
    }
    collectionData.ids.push(id);
    this.ids = collectionData.ids;
    dataModel.collectionsDAO.setCollection(this.getKey(), collectionData, this.getCollectionType());
    return StoreItemProcessing.denormalizeCollection(this.collectionType, collectionData.ids, this.recordDescriptor);
  }

  remove(id) {
    const collectionData = dataModel.collectionsDAO.findCollection(this.getKey());
    const indexToDelete = collectionData.ids.findIndex(existingId => existingId == id);
    collectionData.ids.splice(indexToDelete, 1);
    this.ids = collectionData.ids;
    dataModel.collectionsDAO.setCollection(this.getKey(), collectionData, this.getCollectionType());
    return StoreItemProcessing.denormalizeCollection(this.collectionType, collectionData.ids, this.recordDescriptor);
  }

  normalizeCollection(collection, options) {
    return StoreItemProcessing.normalizeCollection(this.collectionType.toLowerCase(), collection, options);
  }

  getDenormalizedCollection(options, paging = false) {
    let collection = dataModel.collectionsDAO.findCollection(this.getKey());
    if (collection) {
      let clonedCollection = Object.assign({}, collection);

      const collectionKey = StoreItemProcessing.mapToPlural(this.collectionType.toLowerCase());
      clonedCollection[collectionKey] = StoreItemProcessing.denormalizeCollection(collectionKey, collection.ids, options ? options.recordDescriptor : null);
      if (!clonedCollection[collectionKey]) {
        clonedCollection[collectionKey] = [];
      }
      else if (paging && this.page && collection.perPage) {
        const count = this.page * collection.perPage;
        clonedCollection.ids.splice(count);
        clonedCollection[collectionKey].splice(count);
      }
      return clonedCollection;
    }
    else {
      return null;
    }
  }

  getData() {
    return {
      name: this.name, 
      collectionType: this.collectionType, 
      _recordState: this._recordState, 
      ids: this.ids, 
      page: this.page, 
      perPage: this.perPage, 
      searchString: this._searchString, 
      _filter: this._filter ,
      userId: this.userId,
      subKey: this.subKey,
      recordDescriptor: this.recordDescriptor,
      meta: this.meta,
    };
  }

  getDefaultData() {
    return {
      name: this.name, 
      collectionType: this.collectionType, _recordState: '', 
      ids: [], 
      page: 1, 
      perPage: this.perPage, 
      searchString: this._searchString, 
      _filter: this._filter, 
      userId: this.userId,
      subKey: this.subKey,
    };
  }  
  
  findCollection() {
    const collection = dataModel.collectionsDAO.findCollection(this.getKey());
    if (collection) {
      this.ids = collection.ids;
    }
    return collection;
  }

  getName() {
    return this.name;
  }

  getCollectionType() {
    return this.collectionType;
  }

  static generateKey(name, companyID, userId, subKey) {
    return name + (companyID ? '_' + companyID : '') + (userId ? '_' + userId : '') + (subKey ? '_' + subKey : '');
  }

  getKey(){
    return AOCollection.generateKey(this.name, this.companyId, this.userId, this.subKey);
  }

  set searchString(searchString) {
    if (this._searchString != searchString) {
      this._searchString = searchString;
      this._recordState = RecordStates.NEEDS_SYNC;
      dataModel.collectionsDAO.setCollection(this.getKey(), this.getData(), this.getCollectionType());
    }
  }

  isEmpty(obj) {
    if (!obj) {
      return true;
    }
    for(var key in obj) {
      if(obj.hasOwnProperty(key))
        return false;
    }
    return true;
  }

  get searchString() {
    return this._searchString;
  }

  set filter(filter) {
    if (this.isEmpty(filter) && this.isEmpty(this._filter)) {
      return;
    }
    if (!_.isEqual(filter, this._filter)) {
      this._filter = filter ? filter : {};
      this._recordState = RecordStates.NEEDS_SYNC;
      dataModel.collectionsDAO.setCollection(this.getKey(), this.getData(), this.getCollectionType());
    }
  }

  get filter() {
    if (this._filter && typeof this._filter == 'object') {
      return { ...this._filter };
    }
    else return this._filter;
  }

  setFilter(filter = {}){
    if (this.isEmpty(filter) && this.isEmpty(this._filter)) {
      return;
    }
    if (filter != this._filter || !filter) {
      this._filter = filter ? filter : {};
      this._recordState = RecordStates.NEEDS_SYNC;
      this.ids = [];
      this.page = 1;
      dataModel.collectionsDAO.setCollection(this.getKey(), this.getData(), this.getCollectionType());
    }
  }

  getFilter() {
    return this.filter;
  }

  reset() {
    this.searchString = null;
    this._filter = {};
    dataModel.collectionsDAO.setCollection(this.getKey(), this.getDefaultData(), this.getCollectionType());
  }

  _shouldRefreshItem(itemType, item, options) {
    if (!item || (options && options.force)) {
      return true;
    }
    else {
      if (!options || !options.recordDescriptor) {
        if (item._recordState == RecordStates.NEEDS_ATTRIBUTE_SYNC || item._recordState == RecordStates.NEEDS_SYNC) {
          return true;
        }
      }
      if (options && options.recordDescriptor) {
        let keys = Object.keys(options.recordDescriptor);
        let recordDescriptor = null;
        if (keys.length == 1) {
          recordDescriptor = options.recordDescriptor[keys[0]];
        }
        else {
          recordDescriptor = options.recordDescriptor;
        }
        if (!recordDescriptor) {
          if (item._recordState == RecordStates.NEEDS_ATTRIBUTE_SYNC || item._recordState == RecordStates.NEEDS_SYNC) {
            return true;
          }
        }
        else if (!StoreItemProcessing.isComplete(itemType, item.id, recordDescriptor, options ? options.checkArrays : false)) {
          return true;
        }
        else {
          return false;
        }
      }
    }
  }

  needsFetch(collection, options, paging) {
    // If cached search string differs from current request, we need to refetch.
    const cachedSearch = collection && (collection.searchString != null ? collection.searchString : '');
    const currentSearch = this._searchString != null ? this._searchString : '';
    if (cachedSearch !== currentSearch) {
      return true;
    }
    if (
      !collection || !collection.ids || collection.totalRecords == undefined || collection.totalRecords == null || (!collection.ids.length && collection.totalRecords > 0) ||
      (collection._recordState && (collection._recordState == RecordStates.NEEDS_SYNC || collection._recordState == RecordStates.PENDING)) || collection._recordState == undefined ||

       (options && options.force)
      ) {
      return true;
    }
    else {
      let pagedIds = collection.ids.length;
      if (paging) {
        // collection.perPage comes from the persisted record, and _finishFetch never overwrites a
        // stored value (see the perPage line there). A session that cached a FRACTIONAL page size
        // before the POR nav was fixed keeps it, and page * 16.317 (32.63) always exceeds the 32
        // ids actually loaded, so every page looks short and refetches. Flooring is a no-op for the
        // integer page sizes everything else stores, and for null/undefined.
        const pageSize = Math.floor(collection.perPage);
        pagedIds = Math.min(collection.totalRecords != null && collection.totalRecords != undefined ? collection.totalRecords : (this.page * pageSize), this.page * pageSize);  // Use this.page as it is the requested page.
      }
      
      // For infinite scroll, check if we have fewer items than total available
      if (this.name === 'ProjectSummary' && collection.ids.length < collection.totalRecords) {
        return true;
      }
      
      if (pagedIds > collection.ids.length) {
        return true;
      }
      for (let itemId of collection.ids) {
        const item = StoreItemProcessing.findItem(this.collectionType, itemId);
        if (!item) {
          return true;
        }
        else {
          if (item._recordState && (item._recordState == RecordStates.NEEDS_SYNC || item._recordState == RecordStates.PENDING)) {
            return true;
          }

          // check the item itself
          if (options && options.checkArrays) {
            if (this._shouldRefreshItem(this.collectionType.toLowerCase(), item, options)) {
              return true;
            }
          }
          pagedIds--;
          if (pagedIds == 0) {
            break;
          }
        }
      }

      return false;
    }
  }

  _markState(collection, state) {
    let clonedCollection;
    if (!collection) {
      clonedCollection = { name: this.getName(), _recordState: state, ids: [] };
    }
    else{
      clonedCollection = Object.assign({}, collection);
      clonedCollection._recordState = state;
    }

    dataModel.collectionsDAO.setCollection(this.getKey(), clonedCollection, this.getCollectionType());

    return clonedCollection;
  }

  markPending(collection) {
    return this._markState(collection, RecordStates.PENDING);
  }

  /** Interface for methods to be implemented by subclass */
  markNeedsSync(collection) {
    return this._markState(collection, RecordStates.NEEDS_SYNC);
  }

  markSynced(collection) {
    return this._markState(collection, RecordStates.SYNCED);
  }

  markError(collection) {
    return this._markState(collection, RecordStates.ERROR);
  }

  markDirty(collection, dirtyState) {
    let clonedCollection;
    if (!collection) {
      clonedCollection = { name: this.getName(), _dirty: dirtyState, ids: [] };
    }
    else{
      clonedCollection = Object.assign({}, collection);
      clonedCollection._dirty  = dirtyState;
    }
    clonedCollection._dirty = dirtyState;

    dataModel.collectionsDAO.setCollection(this.getKey(), clonedCollection, this.getCollectionType());

    return clonedCollection;
  }

  _finishFetch(collection, response, options, paging, overrideFinish) {
    if (response.offline) {
      collection = this.markNeedsSync();
      return Promise.resolve(response);
    }
    else if (response.error) {
      this.markError();
      dataModel.displayHTTPError('Getting ' + this.name, response.error, false);
      return Promise.resolve(this.getDenormalizedCollection(collection, options));
    }
    else {
      collection = this.markSynced(collection);
      let ids = StoreItemProcessing.normalizeAndSyncCollectionToStore(this.collectionType, response);

      if (options && options.clear) {
      }
      else if (paging) {
        // TODO: all collections pass clear on fetchCurrent(), otherwise sort order is not maintained
        ids = miscFunctions.mergeArrays(collection.ids, ids);
      }
      this.ids = collection.ids = ids;
      let totalRecords = null;
      if (response.meta) {
        for (let key in response.meta){
          if (key.indexOf('total_') == 0 && key != 'total_pages') {
            totalRecords = parseInt(response.meta[key], 10);
          }
        }
      } else {
        // assume full collection
        totalRecords = collection.ids.length;
      }
      const newCollection = { 
        _recordState: RecordStates.SYNCED,
        name: this.getName(), 
        collectionType: collection.collectionType,
        ids: collection.ids, 
        page: this.page,
        perPage: collection && collection.perPage ? collection.perPage : this.perPage,
        searchString: response.searchText || (collection && collection.searchString ? collection.searchString : this.searchString),
        _filter: collection && collection._filter ? collection._filter : this._filter,
        recordDescriptor: collection ? collection.recordDescriptor : undefined,
        meta: response.meta,
        totalRecords: totalRecords,
      };
      dataModel.collectionsDAO.setCollection(this.getKey(), newCollection, this.getCollectionType());
      return this.getDenormalizedCollection(options);
    }
  }

  async _fetchData(serviceCall, options = {}, paging = false, overrideFinish, prefetchCallback) {
    let collection = dataModel.collectionsDAO.findCollection(this.getKey());
    if (!collection) {
      collection = { name: this.getName(), _recordState: null, ids: [] };
    }

    if (dataModel.isOffline || !this.needsFetch(collection, options, paging)) {
      return Promise.resolve(this.getDenormalizedCollection(options, paging));
    }
    else {
      if (prefetchCallback) {
        prefetchCallback(this.getDenormalizedCollection(options  && options.recordDescriptor ? options : null, paging));
      }
      let response = await serviceCall();
      return this._finishFetch(collection, response, options, paging, overrideFinish);
    }
  }

  build() {}

}



// WEBPACK FOOTER //
// ./src/dao/collections/AOCollection.js